<script setup lang="ts">
/**
 * Footer V2 — Accordion controlado.
 *
 * O pai gerencia o estado via v-model:open. Animação segue o padrão
 * usado em components/sections/TabsFAQ.vue (Web Animations API):
 *   - Abrir: 500ms ease (max-height 0 → scrollHeight, opacity 0 → 1)
 *   - Fechar: 400ms ease (max-height scrollHeight → 0, opacity 1 → 0)
 */

const props = defineProps<{
  /** Texto do header (ex: "Atalho", "Informações", "Sobre nós") */
  title: string;
  /** Estado controlado de aberto/fechado */
  open: boolean;
  /** Id estável para wiring de aria-controls (opcional, gerado se omitido) */
  id?: string;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const uid = useId();
const panelId = computed(() => `footer-v2-acc-${props.id ?? uid}`);
const headerId = computed(() => `${panelId.value}-header`);
const panelRef = ref<HTMLElement | null>(null);

// Snapshot não-reativo: avaliado uma vez no setup (SSR-safe, sem onMounted).
// inert no template assume o bloqueio de foco/interação quando fechado.
const initialPanelStyle = !props.open
  ? 'max-height: 0; overflow: hidden; opacity: 0;'
  : undefined;

function toggle() {
  emit('update:open', !props.open);
}

function animatePanel(opening: boolean) {
  const el = panelRef.value;

  if (!el) return;

  el.getAnimations().forEach((a) => a.cancel());
  el.style.overflow = 'hidden';
  el.style.willChange = 'max-height, opacity';

  if (opening) {
    const height = el.scrollHeight;

    // eslint-disable-next-line no-void
    void el.offsetHeight;

    const animation = el.animate(
      [
        { maxHeight: '0px', opacity: 0 },
        { maxHeight: `${height}px`, opacity: 1 },
      ],
      { duration: 500, easing: 'ease', fill: 'forwards' },
    );

    animation.onfinish = () => {
      // libera para conteúdo dinâmico (links de termos chegando depois, etc.)
      el.style.maxHeight = 'none';
      el.style.overflow = '';
      el.style.willChange = '';
    };
  } else {
    const height = el.scrollHeight;

    const animation = el.animate(
      [
        { maxHeight: `${height}px`, opacity: 1 },
        { maxHeight: '0px', opacity: 0 },
      ],
      { duration: 400, easing: 'ease', fill: 'forwards' },
    );

    animation.onfinish = () => {
      el.style.maxHeight = '0px';
      el.style.opacity = '0';
      el.style.willChange = '';
    };
  }
}

watch(
  () => props.open,
  (isOpen) => {
    nextTick(() => animatePanel(isOpen));
  },
  { flush: 'post' },
);
</script>

<template>
  <div class="footer-v2-accordion">
    <button
      :id="headerId"
      :aria-controls="panelId"
      :aria-expanded="open"
      class="footer-v2-accordion__header"
      type="button"
      @click="toggle"
    >
      <span class="footer-v2-accordion__title">{{ title }}</span>

      <span class="footer-v2-accordion__chevron-wrap">
        <svg
          aria-hidden="true"
          class="footer-v2-accordion__chevron"
          :class="{ 'footer-v2-accordion__chevron--open': open }"
          fill="none"
          height="6"
          viewBox="-0.47 0 11 6"
          width="11"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.03822 6L5.51805 5.52017L9.59448 1.44374L10.0764 0.961784L9.11465 3.98414e-07L8.63482 0.47983L5.03822 4.07643L1.44161 0.481953L0.961783 4.20409e-08L0 0.961783L0.47983 1.44161L4.55626 5.51805L5.03822 6Z"
            fill="white"
          />
        </svg>
      </span>
    </button>

    <div
      :id="panelId"
      ref="panelRef"
      :aria-hidden="!open"
      :aria-labelledby="headerId"
      class="footer-v2-accordion__panel"
      :inert="!open"
      role="region"
      :style="initialPanelStyle"
    >
      <div class="footer-v2-accordion__content">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-accordion {
  @apply w-full flex flex-col;

  &__header {
    @apply w-full h-[30px] flex items-center justify-between;
    padding: 3px 12px 3px 12px;
    border-radius: 6.132px;
    background: linear-gradient(
      270deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.05) 49.52%
    );
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: linear-gradient(
        270deg,
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.08) 49.52%
      );
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.6);
      outline-offset: 2px;
    }
  }

  &__title {
    color: #fff;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
  }

  &__chevron-wrap {
    display: flex;
    width: 20px;
    height: 20px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.1);
  }

  &__chevron {
    @apply flex-shrink-0;
    width: 11px;
    height: 6px;
    transition: transform 0.25s ease;
    transform: rotate(0deg);

    &--open {
      transform: rotate(180deg);
    }
  }

  &__panel {
    overflow: hidden;
    /* will-change é aplicado dinamicamente em animatePanel() apenas
       durante a transição, evitando promover todos os painéis a
       camadas GPU permanentemente. */
  }

  &__content {
    padding-top: 8px;
  }
}
</style>
